const TOOLBAR_SELECTOR = '.raid-control-mobile-toolbar.ios-standalone-portal';
const CHAT_INPUT_SELECTOR = [
  '.raid-control-room.mobile .raid-control-composer textarea',
  '.raid-control-mobile-chat-v2-composer textarea',
].join(',');
const TOOLBAR_OFFSET_PROPERTY = '--raid-control-toolbar-visual-offset';
const TOOLBAR_TOP = 'calc(env(safe-area-inset-top) + 8px + var(--raid-control-toolbar-visual-offset))';
const TOOLBAR_VIEWPORT_LOCK_ENABLED = true;
const VISUAL_VIEWPORT_EVENT_OPTIONS = { capture: true, passive: true };
const TOOLBAR_POSITION_EPSILON = 0.5;

let installedCleanup = null;

function isIosStandalone() {
  return typeof window !== 'undefined' && window.navigator.standalone === true;
}

function readPixelValue(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readVisualViewportOffsetTop() {
  const offsetTop = Number(window.visualViewport?.offsetTop || 0);
  return Number.isFinite(offsetTop) ? Math.max(0, offsetTop) : 0;
}

function readToolbarBaseTop(toolbar) {
  const computedTop = readPixelValue(window.getComputedStyle(toolbar).top);
  if (computedTop == null) return null;

  const appliedOffset = toolbar.style.top === TOOLBAR_TOP
    ? readPixelValue(toolbar.style.getPropertyValue(TOOLBAR_OFFSET_PROPERTY)) || 0
    : 0;

  return computedTop - appliedOffset;
}

function setToolbarVisualOffset(toolbar, offsetTop) {
  const offsetValue = `${offsetTop}px`;
  if (toolbar.style.getPropertyValue(TOOLBAR_OFFSET_PROPERTY) !== offsetValue) {
    toolbar.style.setProperty(TOOLBAR_OFFSET_PROPERTY, offsetValue);
  }
}

function applyToolbarOffset(toolbar) {
  const offsetTop = readVisualViewportOffsetTop();
  const datasetValue = String(Math.round(offsetTop));
  const baseTop = readToolbarBaseTop(toolbar);

  setToolbarVisualOffset(toolbar, offsetTop);
  if (toolbar.style.top !== TOOLBAR_TOP) {
    toolbar.style.setProperty('top', TOOLBAR_TOP);
  }

  let appliedOffset = offsetTop;
  if (baseTop != null) {
    const renderedTop = toolbar.getBoundingClientRect().top;
    const correction = baseTop - renderedTop;

    if (Math.abs(correction) > TOOLBAR_POSITION_EPSILON) {
      appliedOffset = Math.max(0, offsetTop + correction);
      setToolbarVisualOffset(toolbar, appliedOffset);
    }
  }

  if (toolbar.dataset.visualViewportOffsetTop !== datasetValue) {
    toolbar.dataset.visualViewportOffsetTop = datasetValue;
  }

  const appliedDatasetValue = String(Math.round(appliedOffset));
  if (toolbar.dataset.visualViewportAppliedOffsetTop !== appliedDatasetValue) {
    toolbar.dataset.visualViewportAppliedOffsetTop = appliedDatasetValue;
  }
}

export function installMobileChatToolbarViewportLock() {
  if (!TOOLBAR_VIEWPORT_LOCK_ENABLED) return () => {};

  if (installedCleanup || typeof window === 'undefined' || typeof document === 'undefined') {
    return installedCleanup || (() => {});
  }

  const visualViewport = window.visualViewport;
  let animationFrame = 0;
  let observedToolbar = null;
  let toolbarObserver = null;

  const observeToolbar = (toolbar) => {
    if (toolbar === observedToolbar) return;
    toolbarObserver?.disconnect();
    toolbarObserver = null;
    observedToolbar = toolbar;

    if (!toolbar || typeof MutationObserver === 'undefined') return;
    toolbarObserver = new MutationObserver(() => queueSync());
    toolbarObserver.observe(toolbar, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  };

  const syncGeometryToVisualViewport = () => {
    if (!isIosStandalone()) return;
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    observeToolbar(toolbar);
    if (!toolbar) return;
    applyToolbarOffset(toolbar);
  };

  const queueSync = () => {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(() => {
      animationFrame = 0;
      syncGeometryToVisualViewport();
    });
  };

  const syncNowAndNextFrame = () => {
    syncGeometryToVisualViewport();
    queueSync();
  };

  const handleChatInputEvent = (event) => {
    if (event.target instanceof HTMLElement && event.target.matches(CHAT_INPUT_SELECTOR)) {
      syncNowAndNextFrame();
    }
  };

  visualViewport?.addEventListener('resize', syncNowAndNextFrame, VISUAL_VIEWPORT_EVENT_OPTIONS);
  visualViewport?.addEventListener('scroll', syncNowAndNextFrame, VISUAL_VIEWPORT_EVENT_OPTIONS);
  document.addEventListener('focusin', handleChatInputEvent, true);
  document.addEventListener('focusout', handleChatInputEvent, true);
  document.addEventListener('input', handleChatInputEvent, true);
  window.addEventListener('pageshow', syncNowAndNextFrame, { passive: true });
  window.addEventListener('hashchange', syncNowAndNextFrame, { passive: true });

  syncGeometryToVisualViewport();

  installedCleanup = () => {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    toolbarObserver?.disconnect();
    toolbarObserver = null;
    observedToolbar = null;
    visualViewport?.removeEventListener('resize', syncNowAndNextFrame, VISUAL_VIEWPORT_EVENT_OPTIONS);
    visualViewport?.removeEventListener('scroll', syncNowAndNextFrame, VISUAL_VIEWPORT_EVENT_OPTIONS);
    document.removeEventListener('focusin', handleChatInputEvent, true);
    document.removeEventListener('focusout', handleChatInputEvent, true);
    document.removeEventListener('input', handleChatInputEvent, true);
    window.removeEventListener('pageshow', syncNowAndNextFrame);
    window.removeEventListener('hashchange', syncNowAndNextFrame);
    installedCleanup = null;
  };

  return installedCleanup;
}
